import React, { useState, useEffect } from "react";
import {
  AddButton,
  SaveBtn,
  TopHeading,
  Wrapper,
} from "../../../../common/index.style";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Card,
  Statistic,
  Descriptions,
  Alert,
  Result,
  Tag,
  Space,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { getPublisher, resetPublisher } from "../../../../redux/publisherSlice";
import {
  addSettlement,
  setSettlement,
} from "../../../../redux/settlementSlice";
import { TbCurrencyDollar } from "react-icons/tb";
import { handleSettlementMinus } from "../../../../utils/helper";
import { formatPrice, getCurrencySymbol } from "../../../../utils/currency";
import UserFormList from "./userFormList";
import { ApiRequests } from "../../../../services/ApiRequest";

const Settle = () => {
  const [confirm, setConfirm] = useState(false);
  const [settle, setSettle] = useState(false);
  const [owedUsd, setOwedUsd] = useState(null);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(null);

  const dispatch = useDispatch();
  const { id } = useParams();
  const store = useSelector((store) => store);
  const { publisher, loading: ploading } = store?.publisher ?? {};
  const { settlement, loading } = store?.settlement ?? {};

  useEffect(() => {
    if (id) {
      dispatch(getPublisher({ publisherId: id }));
    }

    return () => {
      dispatch(resetPublisher());
    };
  }, [id]);

  const publisherCurrency = publisher?.stripe?.currency?.toLowerCase?.();
  const balance = publisher?.balance ?? 0;

  useEffect(() => {
    setOwedUsd(null);
    setFxError(null);
    if (!publisher?.stripe?.currency || ploading) return;

    if (publisherCurrency === "usd") {
      setOwedUsd(balance);
      return;
    }

    if (balance === 0) {
      setOwedUsd(0);
      return;
    }

    let cancelled = false;
    setFxLoading(true);
    ApiRequests.getOwedInUsd(balance, publisherCurrency)
      .then((res) => {
        if (cancelled) return;
        const value = res?.data?.usd_amount ?? res?.data?.usdAmount ?? null;
        setOwedUsd(typeof value === "number" ? value : null);
        setFxError(value == null ? "Unavailable" : null);
      })
      .catch((err) => {
        if (!cancelled) {
          setOwedUsd(null);
          const msg = err?.response?.data?.message;
          setFxError(typeof msg === "string" ? msg : "Failed to convert");
        }
      })
      .finally(() => {
        if (!cancelled) setFxLoading(false);
      });

    return () => { cancelled = true; };
  }, [publisher?.stripe?.currency, balance, ploading, publisherCurrency]);

  const onSubmitSettlement = (values) => {
    dispatch(setSettlement({ ...values }));
    setSettle(true);
  };

  const currencyLabel = publisher?.stripe?.currency
    ? publisher.stripe.currency.toUpperCase()
    : "N/A";

  const formatInCurrency = (number) => {
    const sym = getCurrencySymbol(publisherCurrency || 'usd');
    if (number < 0) return `-${sym}${formatPrice(-1 * number, publisherCurrency)}`;
    return `${sym}${formatPrice(number, publisherCurrency)}`;
  };

  const owedValue = publisher?.balance
    ? formatInCurrency(publisher.balance)
    : `${currencyLabel} ${formatPrice(0, publisherCurrency)}`;

  const owedIsPositive = balance > 0;

  // Owed USD display
  const owedUsdDisplay = fxLoading
    ? "Loading..."
    : fxError
    ? fxError
    : owedUsd != null
    ? owedUsd < 0
      ? "-" + (-1 * owedUsd).toFixed(2)
      : owedUsd.toFixed(2)
    : "—";

  // Post-confirmation success view
  if (confirm) {
    return (
      <Wrapper>
        <Result
          status="success"
          title={`${getCurrencySymbol(publisherCurrency || 'usd')}${formatPrice(settlement?.settledAmount, publisherCurrency)} settlement saved`}
          subTitle={`Settlement recorded for ${publisher?.name}. Please verify this payment has occurred in Stripe.`}
          extra={[
            <AddButton key="back" to="/publisher?owedFilter=positive">
              Back to Publishers
            </AddButton>,
          ]}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <TopHeading>
        {ploading ? "Loading..." : "Settle " + publisher?.name}
      </TopHeading>

      {/* Hero: Owed to Publisher */}
      <Row gutter={[24, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={10}>
          <Card
            style={{
              borderLeft: `4px solid ${owedIsPositive ? "#cf1322" : "#52c41a"}`,
            }}
          >
            <Statistic
              title={
                <span style={{ fontSize: 14 }}>
                  Owed to Publisher{" "}
                  <Tag color="default">{currencyLabel}</Tag>
                </span>
              }
              value={owedValue}
              valueStyle={{
                fontSize: 32,
                fontWeight: 700,
                color: owedIsPositive ? "#cf1322" : "#52c41a",
              }}
            />
            {publisherCurrency !== "usd" && (
              <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>
                USD equivalent: <strong>{owedUsdDisplay}</strong>
              </div>
            )}
          </Card>
        </Col>
        <Col xs={24} md={14}>
          <Card>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="Sold to date">
                {getCurrencySymbol(publisherCurrency || 'usd')}{formatPrice(publisher?.sales ?? 0, publisherCurrency)}
              </Descriptions.Item>
              <Descriptions.Item label="Settled to date">
                {publisher?.settled != null
                  ? handleSettlementMinus(publisher?.settled, publisherCurrency)
                  : handleSettlementMinus(0, publisherCurrency)}
              </Descriptions.Item>
              <Descriptions.Item label="Currency">
                {currencyLabel}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {publisher?.email || "N/A"}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Settlement Form */}
      <Form onFinish={onSubmitSettlement} layout="vertical">
        {settle ? (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <span>
                  Marking <strong>{getCurrencySymbol(publisherCurrency || 'usd')}{formatPrice(settlement?.settledAmount, publisherCurrency)}</strong> as
                  settled for <strong>{publisher?.name}</strong>
                </span>
              }
              description={(() => {
                const remaining = (publisher?.balance ?? 0) - (settlement?.settledAmount ?? 0);
                const isZero = remaining === 0;
                const color = isZero ? "#52c41a" : "#cf1322";
                const sym = getCurrencySymbol(publisherCurrency || 'usd');
                return (
                  <span>
                    After confirmation, you will owe{" "}
                    <strong style={{ fontSize: 18, color }}>
                      {sym}{formatPrice(remaining, publisherCurrency)}
                    </strong>{" "}
                    to {publisher?.name}.
                  </span>
                );
              })()}
            />
            <SaveBtn
              type="button"
              onClick={() => {
                dispatch(
                  addSettlement({
                    settlementData: {
                      amount: settlement?.settledAmount,
                      publisherId: id,
                      transactionId: settlement?.transactionId,
                      paidHistory: settlement?.paidHistory,
                    },
                    setConfirm,
                  })
                );
              }}
            >
              {loading ? "loading..." : "Confirm Settlement"}
            </SaveBtn>
          </>
        ) : (
          <>
            {/* Amount + Transaction ID + Settle button - compact at top */}
            <Card size="small" style={{ maxWidth: 600, marginBottom: 24 }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 12 }}>
                Record Settlement
              </div>
              <Row gutter={12}>
                <Col xs={24} sm={10}>
                  <Form.Item
                    name="settledAmount"
                    label="Amount"
                    rules={[{ required: true, message: "required" }]}
                    style={{ marginBottom: 12 }}
                  >
                    <InputNumber
                      prefix={<TbCurrencyDollar />}
                      precision={2}
                      min={0.01}
                      step={0.01}
                      style={{ width: "100%" }}
                      placeholder="0.00"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={10}>
                  <Form.Item
                    name="transactionId"
                    label="Transaction ID"
                    rules={[{ required: true, message: "required" }]}
                    style={{ marginBottom: 12 }}
                  >
                    <Input placeholder="Stripe txn ID" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={4} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 12 }}>
                  <SaveBtn type="submit" style={{ width: "100%" }}>Settle</SaveBtn>
                </Col>
              </Row>
            </Card>

            {/* Paid users (Add field) section below */}
            <Card title="Paid Users (CSV attachment)" size="small" style={{ maxWidth: 600 }}>
              <UserFormList
                list={publisher?.paidUserTransactionInformation}
              />
            </Card>
          </>
        )}
      </Form>
    </Wrapper>
  );
};

export default Settle;
